import React from 'react';
import { motion } from 'framer-motion';
import { RefreshCw } from 'lucide-react';
import { formatCurrencyCompact, formatStat } from '../utils/formatters';
import { PlatformLogo } from './shared/PlatformLogo';

function LiveCard({ platform, label, metrics }) {
  return (
    <div className="flex items-center gap-3 bg-white/60 rounded-xl px-3 py-2 shrink-0 border border-gray-100/80">
      <PlatformLogo platform={platform} size="md" />
      <div className="flex items-center gap-3">
        {metrics.map((m) => (
          <div key={m.label} className="flex flex-col">
            <span className="text-[9px] text-gray-400 font-medium uppercase leading-none">{m.label}</span>
            <span className="text-[13px] font-bold text-gray-800 leading-tight">{m.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function LivePulseStrip({
  liveGads, liveMeta, liveGa4,
  liveIkas, liveShopify, liveWoo,
  hasIkasConnected, hasShopifyConnected, hasWooConnected,
  isLoading, updatedAt, onRefresh,
}) {
  const cards = [
    liveGads && {
      platform: 'google-ads',
      label: 'Google Ads',
      metrics: [
        { label: 'Cost', value: formatCurrencyCompact(liveGads.cost) },
        { label: 'Clicks', value: formatStat(liveGads.clicks) },
        { label: 'Conv', value: formatStat(liveGads.conversions) },
        { label: 'Value', value: formatCurrencyCompact(liveGads.conversion_value) },
      ],
    },
    liveMeta && {
      platform: 'meta',
      label: 'Meta Ads',
      metrics: [
        { label: 'Spend', value: formatCurrencyCompact(liveMeta.spend) },
        { label: 'Clicks', value: formatStat(liveMeta.clicks) },
        { label: 'Conv', value: formatStat(liveMeta.conversions) },
        { label: 'Value', value: formatCurrencyCompact(liveMeta.conversion_value) },
      ],
    },
    liveGa4 && {
      platform: 'ga4',
      label: 'GA4',
      metrics: [
        { label: 'Sessions', value: formatStat(liveGa4.sessions) },
        { label: 'Users', value: formatStat(liveGa4.users) },
        { label: 'Trans', value: formatStat(liveGa4.transactions) },
        { label: 'Revenue', value: formatCurrencyCompact(liveGa4.revenue) },
      ],
    },
    hasIkasConnected && liveIkas && {
      platform: 'ikas',
      label: 'IKAS',
      metrics: [
        { label: 'Revenue', value: formatCurrencyCompact(liveIkas.revenue) },
        { label: 'Orders', value: formatStat(liveIkas.orders) },
        { label: 'Items', value: formatStat(liveIkas.items) },
      ],
    },
    hasShopifyConnected && liveShopify && {
      platform: 'shopify',
      label: 'Shopify',
      metrics: [
        { label: 'Revenue', value: formatCurrencyCompact(liveShopify.revenue) },
        { label: 'Orders', value: formatStat(liveShopify.orders) },
      ],
    },
    hasWooConnected && liveWoo && {
      platform: 'woo',
      label: 'WooCommerce',
      metrics: [
        { label: 'Revenue', value: formatCurrencyCompact(liveWoo.revenue) },
        { label: 'Orders', value: formatStat(liveWoo.orders) },
      ],
    },
  ].filter(Boolean);

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1, duration: 0.4 }}
      className="mb-6"
    >
      {/* Header row */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <div className="live-dot" />
          <span className="text-[10px] font-bold uppercase tracking-widest text-emerald-600">Today Live</span>
        </div>
        <div className="flex items-center gap-2">
          {updatedAt && (
            <span className="text-[10px] text-gray-400">
              {updatedAt.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
          <button
            onClick={onRefresh}
            disabled={isLoading}
            className="p-1 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
          >
            <RefreshCw size={12} className={`text-gray-400 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Cards row */}
      <div className="flex gap-3 overflow-x-auto scrollbar-hide pb-1">
        {cards.map((card) => (
          <LiveCard key={card.platform} {...card} />
        ))}
      </div>
    </motion.div>
  );
}
